import React, { FC, useContext } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import Svg from "@natera/material/lib/svg";

import SettingsIcon from "../../assets/svg/settings.svg";
import CreditCardIcon from "../../assets/svg/creditCard.svg";
import { IntlContext, PostBillContext } from "@app/provider";
import { getFormattedLocalizeDate } from "@app/utils";
import {
  maskedPaymentMethod,
  convertBalance,
  formatFullTestNames,
} from "@app/postBill/utils";
import {
  InstallmentItem,
  BillingPaymentPlanStatus,
} from "@app/postBill/models";
import { routes } from "@app/routing";
import { Notification } from "@app/components";
import { usePostBillHeapEvent } from "@app/postBill/hooks";
import { HEAP_EVENTS } from "@app/provider/types";

import "./billingInstallmentCard.scss";

const messages = defineMessages({
  billingInstallmentCardTitle: {
    id: "billingInstallmentCardTitle",
    defaultMessage:
      "Autopay (payment {currentInstallmentIndex} of {numInstallments})",
  },
  billingInstallmentCardDueDate: {
    id: "billingInstallmentCardDueDate",
    defaultMessage: "Due date",
  },
  billingInstallmentCardTestNames: {
    id: "billingInstallmentCardTestNames",
    defaultMessage: "Test names",
  },
  billingInstallmentCardRemainingBalance: {
    id: "billingInstallmentCardRemainingBalance",
    defaultMessage: "Remaining balance",
  },
  billingInstallmentCardPaymentMethod: {
    id: "billingInstallmentCardPaymentMethod",
    defaultMessage: "Payment method",
  },
  billingInstallmentCardManagePaymentPlan: {
    id: "billingInstallmentCardManagePaymentPlan",
    defaultMessage: "Manage payment plan",
  },
  billingInstallmentCardPayFullBalance: {
    id: "billingInstallmentCardPayFullBalance",
    defaultMessage: "Pay full balance",
  },
  billingInstallmentCardStatusError: {
    id: "billingInstallmentCardStatusError",
    defaultMessage:
      "There is an issue with your payment method. Update your payment method.",
  },
});

interface BillingTestCardProps {
  installmentItem: InstallmentItem;
}

const BillingInstallmentCard: FC<BillingTestCardProps> = ({
  installmentItem,
}) => {
  const intl = useIntl();
  const { currentLanguage } = useContext(IntlContext);
  const { setPageFrom } = useContext(PostBillContext);
  const navigate = useNavigate();
  const { trackPostBillHeapEvent } = usePostBillHeapEvent();

  const handleManagePaymentPlanButton = async () => {
    trackPostBillHeapEvent({
      eventKey: HEAP_EVENTS.upp_click_billing_manage_payment_plan,
    });

    setPageFrom(routes.billingCenter);
    navigate(routes.billingPaymentPlanDetail, {
      state: {
        paymentPlanId: installmentItem?.paymentPlanId,
      },
    });
  };

  const handlePayFullBalanceButton = async () => {
    trackPostBillHeapEvent({
      eventKey: HEAP_EVENTS.upp_click_billing_payment_plan_pay_full_balance,
    });

    navigate(routes.billingPayment, {
      state: {
        paymentPlanId: installmentItem?.paymentPlanId,
        payFullBalance: true,
        from: routes.billingCenter,
      },
    });
  };

  const renderNotification = () => {
    if (installmentItem.status === BillingPaymentPlanStatus.ERROR) {
      return (
        <Notification
          className="billing-installment-card__notification"
          type="error"
        >
          <p className="billing-installment-card__notification__content">
            {intl.formatMessage(messages.billingInstallmentCardStatusError)}
          </p>
        </Notification>
      );
    }
  };

  return (
    <div className="billing-installment-card">
      <div className="billing-installment-card__display">
        <div className="billing-installment-card__header">
          <div className="billing-installment-card__title">
            {intl.formatMessage(messages.billingInstallmentCardTitle, {
              currentInstallmentIndex:
                installmentItem?.currentInstallmentIndex + 1,
              numInstallments: installmentItem?.numInstallments,
            })}
          </div>
          <div className="billing-installment-card__amount">
            {convertBalance(
              installmentItem?.installmentAmount,
              true,
              installmentItem.currency
            )}
          </div>
        </div>
        <div className="billing-installment-card__content">
          <div className="billing-installment-card__row">
            <span className="billing-installment-card__label">
              {intl.formatMessage(messages.billingInstallmentCardDueDate)}
            </span>
            <span className="billing-installment-card__value">
              {getFormattedLocalizeDate(
                installmentItem?.dueDate,
                currentLanguage
              )}
            </span>
          </div>
          <div className="billing-installment-card__row">
            <span className="billing-installment-card__label">
              {intl.formatMessage(messages.billingInstallmentCardTestNames)}
            </span>
            <span className="billing-installment-card__value">
              {formatFullTestNames(installmentItem?.testName)}
            </span>
          </div>
          <div className="billing-installment-card__row">
            <span className="billing-installment-card__label">
              {intl.formatMessage(
                messages.billingInstallmentCardRemainingBalance
              )}
            </span>
            <span className="billing-installment-card__value">
              {convertBalance(
                installmentItem?.remainingBalance,
                true,
                installmentItem.currency
              )}
            </span>
          </div>
          {installmentItem?.paymentMethod && (
            <div className="billing-installment-card__row">
              <span className="billing-installment-card__label">
                {intl.formatMessage(
                  messages.billingInstallmentCardPaymentMethod
                )}
              </span>
              <span className="billing-installment-card__value">
                {maskedPaymentMethod(installmentItem?.paymentMethod)}
              </span>
            </div>
          )}
        </div>
        {renderNotification()}
        <div className="billing-installment-card__buttons">
          <button className="button" onClick={handleManagePaymentPlanButton}>
            <Svg content={SettingsIcon} className="icon" />
            {intl.formatMessage(
              messages.billingInstallmentCardManagePaymentPlan
            )}
          </button>
          <button className="button" onClick={handlePayFullBalanceButton}>
            <Svg content={CreditCardIcon} className="icon" />
            {intl.formatMessage(messages.billingInstallmentCardPayFullBalance)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BillingInstallmentCard;
